/**
 * CONSULT-STATE ELIGIBILITY -- the frontend MIRROR of the one server rule.
 *
 * QA WALK 10 Sep (D01). The Owed work rail offered "Resume consult" on a
 * CANCELLED appointment; pressing it fired the start signal, the server
 * refused with 409 `appointment_not_actionable`, and the workspace rendered
 * "Start signal failed" over a Retry door that could never succeed. On the
 * same visit the consult NEXT band went on coaching "Close the visit ..." --
 * the last step, over a visit that had been cancelled and can never be
 * closed.
 *
 * THE SERVER REFUSAL IS CORRECT AND IS KEPT byte-for-byte: it is the security
 * boundary, and it stays the only thing that decides what a write may do. The
 * defect was that two SURFACES answered the eligibility question by never
 * asking it -- a draft case sheet was enough to offer Resume, a materialized
 * sheet was enough to coach Close, and neither read the appointment's stored
 * status.
 *
 * ONE RULE, TWO CONSUMERS. The canonical rule lives in
 * `lib/domain/appointment/appointment-status.ts` (the leaf that already owns
 * the status vocabulary, and the module the route's own gate reads). The
 * shipped frontend program CANNOT import from `lib/` -- the production image
 * builds from `frontend/` alone (frontend/tsconfig.app.json) -- so this file
 * MIRRORS it, and a cross-boundary test pins the two identical. That is the
 * established DRAFT_CASE_SHEET_KIND idiom, not a second opinion: if the sets
 * ever drift the pin reds, and the mirror can never quietly become a fork.
 *
 * Pure. No fetch, no router, no clock -- every consumer passes the status in.
 */

/** MIRROR of the server's CONSULT_STARTABLE_STATUSES (the start gate's own
 *  set). Resuming re-fires the same start signal through the same gate, so
 *  the startable set IS the resumable set. */
export const CONSULT_ELIGIBLE_STATUSES: readonly string[] = Object.freeze([
  'arrived',
  'checked_in',
  'fulfilled',
  'completed',
]);

/**
 * TRUE when a consult may lawfully be started or resumed on an appointment in
 * this stored status. Pure and total; fail-closed -- an unknown or malformed
 * status is NOT eligible, so a surface never offers a door on a status it
 * cannot name.
 *
 * ABSENCE IS NOT INELIGIBILITY, and the caller owns that distinction: a
 * pre-corrective server sends no status at all, and a consumer that treated
 * `undefined` as "cancelled" would strip working doors off every row on an
 * older deploy. Consumers therefore ask `isConsultEligibleStatus` only once
 * they HAVE a status (see `consultDoorVerdict`).
 */
export function isConsultEligibleStatus(status: unknown): boolean {
  return (
    typeof status === 'string' && CONSULT_ELIGIBLE_STATUSES.includes(status)
  );
}

/**
 * WHY a consult cannot be started or resumed here, in the doctor's words --
 * or null when it can. The HONEST REPLACEMENT for the futile door: a surface
 * that withholds Resume says what happened to the visit instead of going
 * quiet, so the owed row still names its debt and the coach card still names
 * the state (LES-016 -- never a dead door, and never a silent one).
 *
 * MIRRORS the server's `consultIneligibleReason` string-for-string.
 */
export function consultIneligibleReason(status: unknown): string | null {
  if (isConsultEligibleStatus(status)) return null;
  if (status === 'cancelled') {
    return 'Visit cancelled - this consult cannot be resumed';
  }
  if (status === 'no_show') {
    return 'Marked no-show - this consult cannot be resumed';
  }
  if (typeof status !== 'string' || status.trim() === '') {
    return 'Visit state unavailable - this consult cannot be resumed';
  }
  return 'The patient has not arrived - this consult cannot be started yet';
}

/** What a surface should do about ONE appointment's consult door. */
export type ConsultDoorVerdict =
  /** The status is KNOWN eligible -- open the door. */
  | { readonly kind: 'open' }
  /** The status is KNOWN ineligible -- withhold the door, say `reason`. */
  | { readonly kind: 'blocked'; readonly reason: string }
  /** No status was served (a pre-corrective server). The door stands and the
   *  SERVER re-fences the press -- "unknown" never withholds a lawful door
   *  (the consultDoorForRow posture, verbatim). */
  | { readonly kind: 'unknown' };

/**
 * THE ONE VERDICT both consumers read (Owed work rows; the consult NEXT
 * band). Keeping the tri-state here -- rather than a bare boolean at each
 * call site -- is what stops the two surfaces drifting on the question that
 * actually bites: what to do when the wire says nothing.
 */
export function consultDoorVerdict(
  status: string | null | undefined,
): ConsultDoorVerdict {
  if (status === undefined) return { kind: 'unknown' };
  if (isConsultEligibleStatus(status)) return { kind: 'open' };
  return { kind: 'blocked', reason: consultIneligibleReason(status) as string };
}
